import { IconCheckboxChecked, priceNormalize, Typography, TypographyVariantsEnum } from '@mindly/ui-components';
import { LightningIcon } from 'src/shared/assets/icons/lightningIcon';
import React, { useCallback, useState } from 'react';
import { useElements, useStripe } from '@stripe/react-stripe-js';
import {
  StripeExpressCheckoutElementClickEvent,
  StripeExpressCheckoutElementReadyEvent,
  StripePaymentElement,
} from '@stripe/stripe-js';
import { useTranslation } from 'react-i18next';
import { ExpressCheckout } from 'src/shared/ui/ExpressCheckout';
import { CardPaymentElement } from 'src/shared/ui/CardPaymentElement';
import { useLog } from 'src/shared';

type PaymentCheckoutWidgetProps = {
  chargeAmount: number;
  discountAmount: number;
  amountBeforeDiscount: number;
  clientSecret: string;
  returnUrl: string;
  customerEmail: string;

  onSuccess: () => void;
  onReady: (paymentMethod: 'card' | 'one-click') => void;
  onPayNowButtonClick?: (event?: StripeExpressCheckoutElementClickEvent) => void;
  onPaymentMethodChange: (paymentMethod: 'card' | 'one-click') => void;
};

const PaymentCheckoutWidget: React.FC<PaymentCheckoutWidgetProps> = ({
  chargeAmount,
  discountAmount,
  amountBeforeDiscount,
  clientSecret,
  returnUrl,
  customerEmail,
  onPayNowButtonClick,
  onPaymentMethodChange,
  onReady,
  onSuccess,
}) => {
  const {
    t,
    i18n: { language },
  } = useTranslation('checkout');
  const { logError } = useLog();

  const stripe = useStripe();
  const elements = useElements();

  const [openIndex, setOpenIndex] = useState<number | null>(0);

  const [paymentElementLoading, setPaymentElementLoading] = useState<boolean>(true);
  const [expressCheckoutLoading, setExpressCheckoutLoading] = useState<boolean>(true);

  const [hideExpressCheckout, setHideExpressCheckout] = useState<boolean>(false);

  const onPaymentElementReady = useCallback((event: StripePaymentElement) => {
    setPaymentElementLoading(false);
    onReady?.('card');
  }, []);

  const onExpressCheckoutReady = useCallback((event: StripeExpressCheckoutElementReadyEvent) => {
    if (!event?.availablePaymentMethods) {
      setHideExpressCheckout(true);
    }

    setExpressCheckoutLoading(false);
    onReady?.('one-click');
  }, []);

  const confirmCardPayment = useCallback(async () => {
    if (!elements || !stripe) {
      logError(new Error('PaymentCheckoutWidget: Elements or stripe not found, stripe loading error'));
      throw new Error('Loading error');
    }

    const { error: submitError } = await elements.submit();
    if (submitError) {
      throw new Error(submitError.message);
    }

    const { error } = await stripe?.confirmPayment({
      elements,
      clientSecret,
      redirect: 'if_required',
      confirmParams: {
        return_url: returnUrl,
        payment_method_data: {
          billing_details: {
            email: customerEmail,
          },
        },
      },
    });

    if (error) {
      throw new Error(error.message);
    }
  }, [elements, stripe, clientSecret, returnUrl, customerEmail, logError]);

  const confirmExpressCheckout = useCallback(async () => {
    if (!stripe || !elements) {
      logError(new Error('PaymentCheckoutWidget: Stripe or elements not found, stripe loading error'));
      throw new Error('Loading error');
    }

    const { error } = await stripe.confirmPayment({
      elements: elements,
      clientSecret,
      redirect: 'if_required',
      confirmParams: {
        return_url: returnUrl,
      },
    });

    if (error) {
      throw new Error(error.message);
    }
  }, [stripe, elements, clientSecret, returnUrl, logError]);

  const formatPrice = useCallback(
    (value: number) => {
      return priceNormalize({
        price: value / 100,
        locale: language,
        roundingTo: 2,
        extendedUsd: true,
      });
    },
    [language]
  );

  const accordions = [
    ...(hideExpressCheckout
      ? []
      : [
          {
            title: t('payment_option_one_click'),
            type: 'one-click',
            content: (
              <ExpressCheckout
                onReady={onExpressCheckoutReady}
                onSuccess={onSuccess}
                confirmExpressCheckout={confirmExpressCheckout}
                onClick={onPayNowButtonClick}
                showError={true}
              />
            ),
          },
        ]),
    {
      title: t('payment_option_card'),
      type: 'card',
      content: (
        <CardPaymentElement
          onReady={onPaymentElementReady}
          onSuccess={onSuccess}
          confirmCardPayment={confirmCardPayment}
          onPaymentButtonClick={onPayNowButtonClick}
        />
      ),
    },
  ];

  return (
    <div className={'h-full px-5 pb-5 overflow-y-auto'}>
      <Typography variant={TypographyVariantsEnum.Text24Semi} align={'center'} className={'mb-3'}>
        {t('checkout_title')}
      </Typography>
      <div className="flex flex-col w-full mt-2 gap-1">
        <div className="flex justify-between items-center">
          <Typography variant={TypographyVariantsEnum.Text14Regular} opacity={50}>
            {t('package_name')}
          </Typography>
          <Typography variant={TypographyVariantsEnum.Text14Regular} opacity={50}>
            {formatPrice(amountBeforeDiscount)}
          </Typography>
        </div>
        {discountAmount > 0 && (
          <div className="flex justify-between items-center">
            <Typography variant={TypographyVariantsEnum.Text14Regular} opacity={50}>
              {t('discount_name')}
            </Typography>
            <Typography variant={TypographyVariantsEnum.Text14Regular} opacity={50}>
              -{formatPrice(discountAmount)}
            </Typography>
          </div>
        )}
      </div>

      <div className="my-3">
        <div className="border-b border-borders-divider" />
      </div>

      <div className="flex flex-col gap-2 mb-2">
        <div className="flex justify-between items-center">
          <Typography variant={TypographyVariantsEnum.Text16Semi}>{t('total')}</Typography>
          <Typography variant={TypographyVariantsEnum.Text16Semi} className="!text-[#029cee]">
            {formatPrice(chargeAmount)}
          </Typography>
        </div>
        {discountAmount > 0 && (
          <div className="flex justify-end gap-1 items-center">
            <LightningIcon />
            <Typography variant={TypographyVariantsEnum.Text14Semi} className="!text-[#FF6D47]">
              {t('discount_caption', {
                discount: `${formatPrice(discountAmount)}`,
              })}
            </Typography>
          </div>
        )}
      </div>

      <div className="mt-4 mb-6">
        <div className="border-b border-borders-divider" />
      </div>

      <div>
        <Typography variant={TypographyVariantsEnum.Text18Semi} align={'center'} className={'mb-3'}>
          {t('checkout_subtitle')}
        </Typography>
      </div>

      {/* Move to UI lib */}
      <div className="mt-4 flex flex-col gap-4">
        {accordions.map((acc, idx) => (
          <div key={acc.title} className="rounded-2xl border border-[#2121211a] bg-white overflow-hidden">
            <button
              type="button"
              className="flex items-center w-full px-4 py-4 text-left gap-3 focus:outline-none"
              onClick={() => {
                setOpenIndex(idx);
                onPaymentMethodChange(acc.type as 'card' | 'one-click');
              }}
              disabled={acc.type === 'one-click' ? !!expressCheckoutLoading : !!paymentElementLoading}
            >
              {openIndex === idx ? (
                <IconCheckboxChecked color="black" />
              ) : (
                <span className="w-5 h-5 rounded-full border-2 items-center justify-center transition-colors duration-200 border-[#C1C1C1] bg-white box-border" />
              )}
              <span className="font-normal text-base text-[#212121]">{acc.title}</span>
            </button>
            {openIndex === idx && (
              <div
                className="border-t border-[#2121211a] mx-4 transition-all duration-200 ease-in-out"
                style={{ opacity: openIndex === idx ? 1 : 0, height: openIndex === idx ? '1px' : '0px' }}
              />
            )}
            <div
              className={`overflow-hidden transition-all duration-200 ease-in-out ${openIndex === idx ? 'opacity-100 py-4 px-4' : 'opacity-0 max-h-0 py-0 px-4'}`}
            >
              <div className="text-[#212121] text-base font-normal">{acc.content}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default PaymentCheckoutWidget;
